import { useTranslation } from "react-i18next";
import { useProfileForm } from "../hooks/useProfileForm";
import { useProfile } from "../services/GetProfile";
import Spinner from "@components/common/Spinner";
import ProfileActionButtons from "./ProfileActionButtons";
import ProfileDisplay from "./ProfileDisplay";
import ProfileFormFields from "./ProfileFormFields";
import ProfilePicture from "./ProfilePicture";
import ProfileHeader from "./ProfileHeader";
import type { ProfileModalTypes } from "../types";
import { getFullName } from "../utils/getFullName";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import Button from "@components/common/Button/Button";

const ProfileModal: React.FC<ProfileModalTypes> = ({ HideProfile }) => {
	const { t } = useTranslation();
	const { data, isLoading } = useProfile();
	const { 
		isEditMode, 
		isUpdating, 
		isUploading, 
		form, 
		handleImageChange, 
		onSubmit, 
		handleCancel, 
		handleEdit,
		showEmailChangeWarning,
		handleEmailChangeConfirm,
		handleEmailChangeCancel
	} = useProfileForm(data, t);

	const { control, handleSubmit } = form;

	const fullName = getFullName({ firstName: data?.firstName, lastName: data?.lastName }, t);

	if (isLoading) return <Spinner overlay />;

	return (
		<div className="flex flex-col bg-white relative rounded-lg">
			<ProfileHeader title={t("psdaCommon.profile.profileTitle")} onClose={HideProfile} />

			<form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
				<div className="px-6 pb-6">
					<div className="flex flex-col items-center mb-8">
						<ProfilePicture
							profilePicture={data?.profilePicture}
							isUploading={isUploading}
							onImageChange={handleImageChange}
						/>
						<h3 className="text-xl font-semibold text-gray-900 text-center">{fullName}</h3>
					</div>

					<div className="space-y-6 max-w-lg mx-auto">
						{isEditMode ? (
							<ProfileFormFields control={control} t={t} />
						) : (
							<ProfileDisplay phoneNumber={data?.phoneNumber} emailAddress={data?.emailAddress} />
						)}
					</div>
				</div>

				<ProfileActionButtons
					isEditMode={isEditMode}
					isUpdating={isUpdating}
					onEdit={handleEdit}
					onCancel={handleCancel}
					t={t}
				/>
			</form>

			{/* Email Change Warning Dialog */}
			<Dialog
				open={showEmailChangeWarning}
				onClose={handleEmailChangeCancel}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{t("psdaCommon.profile.emailChangeWarningModal.modalTitle")}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body1">
						{t("psdaCommon.profile.emailChangeWarningModal.modalMessage")}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button 
						onClick={handleEmailChangeCancel}
						variant="dangerTertiary"
					>
						{t("psdaCommon.common.buttons.cancel")}
					</Button>
					<Button 
						onClick={handleEmailChangeConfirm}
						variant="dangerSecondary"
					>
						{t("psdaCommon.profile.emailChangeWarningModal.confirmButton")}	
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default ProfileModal;
